import { useState } from 'react'
import {
  useTenants, useCreateTenant, useDeleteTenant,
  useGeoGroups, useCreateGeoGroup, useDeleteGeoGroup,
  useGeoGroupDevices, useAddDeviceToGeoGroup, useRemoveDeviceFromGeoGroup,
  useGrants, useCreateGrant, useDeleteGrant,
  useDeviceTypes, useCreateDeviceType,
  useAllSupportGrants, useTenantErrors,
} from '@/api/admin'
import { useDevices } from '@/api/devices'
import { useApiClient } from '@/api/client'
import { ListResult } from '@/types'
import { RequirePermission } from '@/components/RequirePermission'
import { useRoles } from '@/hooks/usePermission'

type Tab = 'tenants' | 'geo-groups' | 'grants' | 'device-types' | 'system'

interface Props {
  tenantId: string | null
}

// ── Delete Tenant Dialog ─────────────────────────────────────────────────────

interface DeleteTenantDialogProps {
  tenantSlug: string
  tenantId: string
  onConfirm: () => void
  onCancel: () => void
}

function DeleteTenantDialog({ tenantSlug, tenantId, onConfirm, onCancel }: DeleteTenantDialogProps) {
  const api = useApiClient()
  const [confirmSlug, setConfirmSlug] = useState('')
  const [counts, setCounts] = useState<{ devices: number; fleets: number } | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch resource counts once on mount
  useState(() => {
    Promise.all([
      api.get<ListResult<unknown>>(`/api/v1/tenants/${tenantId}/devices?limit=1`).catch(() => ({ total: 0 })),
      api.get<ListResult<unknown>>(`/api/v1/tenants/${tenantId}/fleets`).catch(() => ({ total: 0 })),
    ]).then(([dev, fl]) => {
      setCounts({ devices: (dev as ListResult<unknown>).total, fleets: (fl as ListResult<unknown>).total })
      setLoading(false)
    })
  })

  const hasResources = counts && (counts.devices > 0 || counts.fleets > 0)
  const canConfirm = confirmSlug === tenantSlug

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        padding: 24,
        width: 480,
        maxWidth: '90vw',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: 'var(--color-error)' }}>
          Delete tenant "{tenantSlug}"
        </h3>

        {loading ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Checking resources…</p>
        ) : hasResources ? (
          <div style={{
            background: 'rgba(var(--color-error-rgb, 220,38,38),0.1)',
            border: '1px solid var(--color-error)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
          }}>
            <strong style={{ color: 'var(--color-error)' }}>Warning: this tenant still has resources.</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--color-text)' }}>
              {counts!.devices > 0 && <li>{counts!.devices} device{counts!.devices !== 1 ? 's' : ''}</li>}
              {counts!.fleets > 0 && <li>{counts!.fleets} fleet{counts!.fleets !== 1 ? 's' : ''}</li>}
            </ul>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-muted)' }}>
              All resources will be permanently deleted.
            </p>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
            This tenant has no devices or fleets. It will be permanently removed.
          </p>
        )}

        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 6 }}>
          Type <strong style={{ color: 'var(--color-text)' }}>{tenantSlug}</strong> to confirm:
        </label>
        <input
          value={confirmSlug}
          onChange={e => setConfirmSlug(e.target.value)}
          placeholder={tenantSlug}
          autoFocus
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'var(--color-surface-2)',
            border: `1px solid ${canConfirm ? 'var(--color-error)' : 'var(--color-border)'}`,
            color: 'var(--color-text)',
            padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13,
            marginBottom: 16,
          }}
          onKeyDown={e => { if (e.key === 'Enter' && canConfirm) onConfirm() }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button
            className="btn"
            disabled={!canConfirm || loading}
            onClick={onConfirm}
            style={{
              background: canConfirm ? 'var(--color-error)' : 'var(--color-surface-2)',
              color: canConfirm ? '#fff' : 'var(--color-text-muted)',
              border: '1px solid var(--color-border)',
              opacity: !canConfirm ? 0.6 : 1,
            }}
          >
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Tenants tab ──────────────────────────────────────────────────────────────

function TenantsTab() {
  const { data, isLoading, error } = useTenants()
  const createTenant = useCreateTenant()
  const deleteTenant = useDeleteTenant()
  const [showCreate, setShowCreate] = useState(false)
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; slug: string } | null>(null)

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    await createTenant.mutateAsync({ slug: slug.trim(), name: name.trim() })
    setSlug('')
    setName('')
    setShowCreate(false)
  }

  function handleDeleteConfirm() {
    if (deleteTarget) {
      deleteTenant.mutate(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  return (
    <div>
      {deleteTarget && (
        <DeleteTenantDialog
          tenantId={deleteTarget.id}
          tenantSlug={deleteTarget.slug}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Tenants</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            {data?.total ?? 0} total
          </p>
        </div>
        <RequirePermission perm="tenants:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
            + New tenant
          </button>
        </RequirePermission>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Slug</label>
            <input
              value={slug}
              onChange={e => setSlug(e.target.value)}
              placeholder="e.g. acme"
              required
              pattern="[a-z0-9-]+"
              style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13, width: 180 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Acme Corp"
              required
              style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13, width: 220 }}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={createTenant.isPending}>
            {createTenant.isPending ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
        </form>
      )}

      {isLoading && <div style={{ color: 'var(--color-text-muted)', padding: 20 }}>Loading…</div>}
      {error && <div style={{ color: 'var(--color-error)', padding: 20 }}>Error: {String(error)}</div>}

      <table className="table">
        <thead>
          <tr>
            <th>Slug</th>
            <th>Name</th>
            <th>ID</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(data?.data ?? []).map(t => (
            <tr key={t.id}>
              <td><code style={{ fontSize: 12 }}>{t.slug}</code></td>
              <td>{t.name}</td>
              <td><code style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{t.id}</code></td>
              <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {new Date(t.created_at).toLocaleDateString()}
              </td>
              <td>
                <RequirePermission perm="tenants:write">
                  <button
                    className="btn btn-ghost"
                    style={{ color: 'var(--color-error)', fontSize: 12 }}
                    onClick={() => setDeleteTarget({ id: t.id, slug: t.slug })}
                  >
                    Delete
                  </button>
                </RequirePermission>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Geo Groups tab ───────────────────────────────────────────────────────────

const inputSm: React.CSSProperties = {
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '6px 10px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
}

function GeoGroupDevicePanel({ tenantId, groupId }: { tenantId: string; groupId: string }) {
  const { data, isLoading } = useGeoGroupDevices(tenantId, groupId)
  const { data: allDevices } = useDevices(tenantId)
  const addDevice = useAddDeviceToGeoGroup(tenantId)
  const removeDevice = useRemoveDeviceFromGeoGroup(tenantId)
  const [selectedDeviceId, setSelectedDeviceId] = useState('')

  const members = data?.data ?? []
  const memberIds = new Set(members.map(d => d.id))
  const candidates = (allDevices?.data ?? []).filter(d => !memberIds.has(d.id))

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedDeviceId) return
    await addDevice.mutateAsync({ groupId, deviceId: selectedDeviceId })
    setSelectedDeviceId('')
  }

  return (
    <div style={{ padding: '10px 16px 14px 32px', background: 'var(--color-surface-2)', borderTop: '1px solid var(--color-border)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Devices in this group ({members.length})
      </div>

      {isLoading && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Loading…</div>}

      {members.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {members.map(d => (
            <span key={d.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              borderRadius: 4, padding: '3px 8px', fontSize: 12, fontFamily: 'var(--font-mono)',
            }}>
              {d.name}
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 14, padding: 0, lineHeight: 1 }}
                title="Remove from group"
                onClick={() => removeDevice.mutate({ groupId, deviceId: d.id })}
              >×</button>
            </span>
          ))}
        </div>
      )}

      {candidates.length > 0 && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            style={{ ...inputSm, minWidth: 220 }}
            value={selectedDeviceId}
            onChange={e => setSelectedDeviceId(e.target.value)}
          >
            <option value="">— Add device —</option>
            {candidates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <button type="submit" className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} disabled={!selectedDeviceId || addDevice.isPending}>
            {addDevice.isPending ? 'Adding…' : 'Add'}
          </button>
        </form>
      )}

      {candidates.length === 0 && members.length === 0 && !isLoading && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No devices available to add.</div>
      )}
    </div>
  )
}

function GeoGroupsTab({ tenantId }: { tenantId: string | null }) {
  const { data, isLoading, error } = useGeoGroups(tenantId)
  const createGroup = useCreateGeoGroup(tenantId ?? '')
  const deleteGroup = useDeleteGeoGroup(tenantId ?? '')
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newParentId, setNewParentId] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const groups = data?.data ?? []
  const groupById = Object.fromEntries(groups.map(g => [g.id, g]))

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    await createGroup.mutateAsync({
      name: newName.trim(),
      parent_id: newParentId || undefined,
    })
    setNewName('')
    setNewParentId('')
    setShowCreate(false)
  }

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage geo groups.</div>
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Geo Groups</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Hierarchical groups for device visibility scoping
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
          + New group
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Group name *</label>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Italy / Milan"
              required
              style={{ ...inputSm, width: 240, padding: '8px 12px', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Parent group</label>
            <select
              style={{ ...inputSm, minWidth: 200, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}
              value={newParentId}
              onChange={e => setNewParentId(e.target.value)}
            >
              <option value="">— Root level —</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={createGroup.isPending}>
            {createGroup.isPending ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
        </form>
      )}

      {isLoading && <div style={{ color: 'var(--color-text-muted)', padding: 20 }}>Loading…</div>}
      {error && <div style={{ color: 'var(--color-error)', padding: 20 }}>Error: {String(error)}</div>}

      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <table className="table" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Name</th>
              <th>Level</th>
              <th>Parent</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <>
                <tr key={g.id}>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 12, padding: 2, lineHeight: 1 }}
                      title={expandedId === g.id ? 'Collapse' : 'Manage devices'}
                      onClick={() => setExpandedId(expandedId === g.id ? null : g.id)}
                    >
                      {expandedId === g.id ? '▾' : '▸'}
                    </button>
                  </td>
                  <td style={{ fontWeight: 500 }}>{g.name}</td>
                  <td style={{ color: 'var(--color-text-muted)' }}>{g.level}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {g.parent_id ? (groupById[g.parent_id]?.name ?? <code style={{ fontSize: 11 }}>{g.parent_id}</code>) : '—'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {new Date(g.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      className="btn btn-ghost"
                      style={{ color: 'var(--color-error)', fontSize: 12 }}
                      onClick={() => {
                        if (confirm(`Delete geo group "${g.name}"?`)) {
                          deleteGroup.mutate(g.id)
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                {expandedId === g.id && (
                  <tr key={`${g.id}-devices`}>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <GeoGroupDevicePanel tenantId={tenantId} groupId={g.id} />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
        {groups.length === 0 && !isLoading && (
          <div className="empty-state" style={{ borderTop: 'none' }}>No geo groups yet.</div>
        )}
      </div>
    </div>
  )
}

// ── User Grants tab ──────────────────────────────────────────────────────────

function GrantsTab({ tenantId }: { tenantId: string | null }) {
  const { data, isLoading, error } = useGrants(tenantId)
  const createGrant = useCreateGrant(tenantId ?? '')
  const deleteGrant = useDeleteGrant(tenantId ?? '')
  const [showCreate, setShowCreate] = useState(false)
  const [userSub, setUserSub] = useState('')
  const [geoGroupId, setGeoGroupId] = useState('')
  const { data: geoGroupsData } = useGeoGroups(tenantId)

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    await createGrant.mutateAsync({
      user_sub: userSub.trim(),
      geo_group_id: geoGroupId || undefined,
    })
    setUserSub('')
    setGeoGroupId('')
    setShowCreate(false)
  }

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage user grants.</div>
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>User Grants</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Control which users can see which devices or geo groups
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
          + New grant
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>User subject (Keycloak sub)</label>
            <input
              value={userSub}
              onChange={e => setUserSub(e.target.value)}
              placeholder="e.g. a1b2c3d4-..."
              required
              style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13, width: 300 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Geo group (optional)</label>
            <select
              value={geoGroupId}
              onChange={e => setGeoGroupId(e.target.value)}
              style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13, width: 220 }}
            >
              <option value="">(all devices in tenant)</option>
              {(geoGroupsData?.data ?? []).map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={createGrant.isPending}>
            {createGrant.isPending ? 'Granting…' : 'Grant access'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
        </form>
      )}

      {isLoading && <div style={{ color: 'var(--color-text-muted)', padding: 20 }}>Loading…</div>}
      {error && <div style={{ color: 'var(--color-error)', padding: 20 }}>Error: {String(error)}</div>}

      <table className="table">
        <thead>
          <tr>
            <th>User subject</th>
            <th>Scope</th>
            <th>Granted by</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(data?.data ?? []).map(gr => (
            <tr key={gr.id}>
              <td><code style={{ fontSize: 11 }}>{gr.user_sub}</code></td>
              <td style={{ fontSize: 12 }}>
                {gr.geo_group_id
                  ? <>Geo group: <code style={{ fontSize: 11 }}>{gr.geo_group_id}</code></>
                  : gr.device_id
                  ? <>Device: <code style={{ fontSize: 11 }}>{gr.device_id}</code></>
                  : 'All devices'}
              </td>
              <td><code style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{gr.created_by}</code></td>
              <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {new Date(gr.created_at).toLocaleDateString()}
              </td>
              <td>
                <button
                  className="btn btn-ghost"
                  style={{ color: 'var(--color-error)', fontSize: 12 }}
                  onClick={() => deleteGrant.mutate(gr.id)}
                >
                  Revoke
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Main Admin page ──────────────────────────────────────────────────────────

// ── Device Types tab ────────────────────────────────────────────────────────

function DeviceTypesTab({ tenantId }: { tenantId: string | null }) {
  const { data, isLoading, error } = useDeviceTypes(tenantId)
  const createDeviceType = useCreateDeviceType(tenantId ?? '')
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [arch, setArch] = useState('armv7l')
  const [os, setOs] = useState('debian')

  const inputStyle: React.CSSProperties = {
    background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    padding: '8px 12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    width: '100%',
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    await createDeviceType.mutateAsync({ name: name.trim(), architecture: arch, os_family: os })
    setName('')
    setShowCreate(false)
  }

  if (!tenantId) {
    return <div style={{ color: 'var(--color-text-muted)', padding: 20 }}>Select a tenant to manage device types.</div>
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Device Types</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Hardware templates — each type maps to a HawkBit DistributionSetType
          </p>
        </div>
        <RequirePermission perm="tenants:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>+ New type</button>
        </RequirePermission>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 16,
            marginBottom: 16,
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr auto auto',
            gap: 12,
            alignItems: 'flex-end',
          }}
        >
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. modberry-500" required style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Architecture</label>
            <input value={arch} onChange={e => setArch(e.target.value)} placeholder="e.g. armv7l" required style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>OS family</label>
            <input value={os} onChange={e => setOs(e.target.value)} placeholder="e.g. debian" required style={inputStyle} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={createDeviceType.isPending} style={{ marginTop: 20 }}>
            {createDeviceType.isPending ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)} style={{ marginTop: 20 }}>Cancel</button>
        </form>
      )}

      {isLoading && <div style={{ color: 'var(--color-text-muted)', padding: 20 }}>Loading…</div>}
      {error && <div style={{ color: 'var(--color-danger)', padding: 20 }}>Error loading device types.</div>}

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Architecture</th>
            <th>OS</th>
            <th>HawkBit DSType</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {(data?.data ?? []).map(dt => (
            <tr key={dt.id}>
              <td style={{ fontWeight: 500 }}>{dt.name}</td>
              <td><code style={{ fontSize: 12 }}>{dt.architecture}</code></td>
              <td>{dt.os_family}</td>
              <td>
                {dt.hawkbit_ds_type_id
                  ? <span style={{ color: 'var(--color-success)', fontSize: 12 }}>✓ ID {dt.hawkbit_ds_type_id}</span>
                  : <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>syncing…</span>
                }
              </td>
              <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{new Date(dt.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── System Health tab (platform-admin: cross-tenant errors + active grants) ──

function SystemTab() {
  const { data: errors, isLoading: errLoading } = useTenantErrors()
  const { data: grants, isLoading: grantsLoading } = useAllSupportGrants()
  const { data: tenantsData } = useTenants()

  const tenantByID = Object.fromEntries(
    (tenantsData?.data ?? []).map(t => [t.id, t])
  )

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius)',
    padding: 20,
    marginBottom: 24,
  }
  const sectionLabel: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em',
    color: 'var(--color-text-muted)', marginBottom: 14,
  }

  return (
    <div>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={sectionLabel}>Tenant Onboarding Errors</div>
          {!errLoading && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3,
              background: (errors?.length ?? 0) > 0 ? 'var(--color-danger-dim)' : 'var(--color-success-dim)',
              color: (errors?.length ?? 0) > 0 ? 'var(--color-danger)' : 'var(--color-success)',
              border: `1px solid ${(errors?.length ?? 0) > 0 ? 'rgba(255,69,96,0.25)' : 'rgba(0,229,182,0.25)'}`,
            }}>
              {errors?.length ?? 0} errors
            </span>
          )}
        </div>
        {errLoading && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading…</div>}
        {!errLoading && (errors?.length ?? 0) === 0 && (
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>All tenants onboarded successfully.</div>
        )}
        {(errors ?? []).length > 0 && (
          <table>
            <thead>
              <tr><th>Tenant</th><th>Onboarding error</th><th>Last attempt</th></tr>
            </thead>
            <tbody>
              {(errors ?? []).map(e => (
                <tr key={e.tenant_id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{e.tenant_name}</div>
                    <code style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{e.tenant_slug}</code>
                  </td>
                  <td>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 11,
                      color: 'var(--color-danger)', background: 'var(--color-danger-dim)',
                      border: '1px solid rgba(255,69,96,0.2)',
                      padding: '6px 10px', borderRadius: 3,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxWidth: 480,
                    }}>
                      {e.onboarding_error}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {e.onboarded_at ? new Date(e.onboarded_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={cardStyle}>
        <div style={{ ...sectionLabel, marginBottom: 14 }}>Active Support Grants (All Tenants)</div>
        {grantsLoading && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading…</div>}
        {!grantsLoading && (grants?.length ?? 0) === 0 && (
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No active support grants.</div>
        )}
        {(grants ?? []).length > 0 && (
          <table>
            <thead>
              <tr><th>Tenant</th><th>Sections</th><th>Granted by</th><th>Note</th><th>Expires</th></tr>
            </thead>
            <tbody>
              {(grants ?? []).map(g => (
                <tr key={g.id}>
                  <td>
                    {tenantByID[g.tenant_id] ? (
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{tenantByID[g.tenant_id].name}</div>
                        <code style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{tenantByID[g.tenant_id].slug}</code>
                      </div>
                    ) : (
                      <code style={{ fontSize: 11 }} title={g.tenant_id}>{g.tenant_id.slice(0, 8)}…</code>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {g.sections.map(s => (
                        <span key={s} className="badge badge-active" style={{ textTransform: 'none', letterSpacing: 0 }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <code
                      style={{ fontSize: 11, color: 'var(--color-text-muted)', cursor: 'default' }}
                      title={g.granted_by}
                    >
                      {g.granted_by.length > 16 ? g.granted_by.slice(0, 8) + '…' : g.granted_by}
                    </code>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)', maxWidth: 200 }}>{g.note ?? '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {g.expires_at ? new Date(g.expires_at).toLocaleString() : 'Until revoked'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ── Main Admin page ──────────────────────────────────────────────────────────

export function Admin({ tenantId }: Props) {
  const [tab, setTab] = useState<Tab>('tenants')
  const roles = useRoles()
  const isPlatformAdmin = roles.includes('platform-admin')

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: tab === t ? 600 : 400,
    color: tab === t ? 'var(--color-accent)' : 'var(--color-text-muted)',
    borderBottom: `2px solid ${tab === t ? 'var(--color-accent)' : 'transparent'}`,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderBottomWidth: 2,
    borderBottomStyle: 'solid',
  })

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Administration</h1>
          <p className="page-subtitle">Platform management — tenants, visibility and access control</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--color-border)', marginBottom: 24 }}>
        {isPlatformAdmin && <button style={tabStyle('tenants')} onClick={() => setTab('tenants')}>Tenants</button>}
        <button style={tabStyle('geo-groups')} onClick={() => setTab('geo-groups')}>Geo Groups</button>
        <button style={tabStyle('grants')} onClick={() => setTab('grants')}>User Grants</button>
        <button style={tabStyle('device-types')} onClick={() => setTab('device-types')}>Device Types</button>

        {isPlatformAdmin && (
          <button style={tabStyle('system')} onClick={() => setTab('system')}>System Health</button>
        )}
      </div>

      {tab === 'tenants' && isPlatformAdmin && <TenantsTab />}
      {tab === 'geo-groups' && <GeoGroupsTab tenantId={tenantId} />}
      {tab === 'grants' && <GrantsTab tenantId={tenantId} />}
      {tab === 'device-types' && <DeviceTypesTab tenantId={tenantId} />}

      {tab === 'system' && isPlatformAdmin && <SystemTab />}
    </div>
  )
}
